import React, { FunctionComponent, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { Loader } from 'semantic-ui-react';
import { TradeIn } from '@upstart/harmony/lib/legos/dealTypes';
import { DealerWithFlags } from '@upstart/harmony/lib/legos/dealerTypes';
import { canUpdateTradeIns, validateTradeIns } from 'util/deskingUtils';
import { CrmType } from '@upstart/harmony/lib/legos/constants';
import { ConfirmationModal } from '@upstart/shared-components';
import CloseIcon from '../../Icons/Close';
import './TradeInsModal.scss';
import { TradeInList } from './TradeInsList';
import { ErrorMap, TradeInField } from './TradeInsTableRow';
import { fauxTradeId } from '../../../util/constants';
import { scrollableModalStyles } from '../../PencilEditor/PencilOfferList/EditIncentives';
import { isUpdatingExistingTradeIns } from './utils';

const MAX_TRADE_INS = 3;

interface TradeInsModalProps {
  initialTradeIns: TradeIn[];
  dealer: DealerWithFlags;
  updateTradeIns: (trade: TradeIn[]) => Promise<void>;
  isOpen: boolean;
  onClose: () => void;
  useSoftDelete?: boolean;
  tradeInFields: TradeInField[];
  header?: string;
  clearFauxTradeInOnDelete?: boolean;
  alwaysAllowTradeInUpdates?: boolean;
}

const TradeInsModal: FunctionComponent<TradeInsModalProps> = props => {
  const {
    initialTradeIns,
    dealer,
    tradeInFields,
    header,
    updateTradeIns,
    isOpen,
    onClose,
    clearFauxTradeInOnDelete,
    alwaysAllowTradeInUpdates,
  } = props;
  const dealerId = dealer.id;
  const [tradeIns, setTradeIns] = useState<TradeIn[]>([]);
  const [updatedTradeIns, setUpdatedTradeIns] = useState<TradeIn[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<ErrorMap>({});
  const [
    isDeleteConfirmationModalOpen,
    setIsDeleteConfirmationModalOpen,
  ] = useState(false);

  const allowTradeInUpdates =
    alwaysAllowTradeInUpdates || canUpdateTradeIns(dealer);

  useEffect(() => {
    setTradeIns([...initialTradeIns]);
    setUpdatedTradeIns([...initialTradeIns]);
    setValidationErrors({});
  }, [initialTradeIns]);

  const onAdd = () => {
    if (updatedTradeIns.length >= MAX_TRADE_INS) {
      return;
    }
    const newList = [...updatedTradeIns, { tradeInAllowance: 0, tradeACV: 0 }];
    setTradeIns(newList);
    setUpdatedTradeIns(newList);
  };

  const getFauxTradeIn = () => {
    let fauxTradeIn = initialTradeIns?.find(
      tradeIn => tradeIn.id === fauxTradeId,
    );
    if (!fauxTradeIn || clearFauxTradeInOnDelete) {
      fauxTradeIn = {
        id: 'faux-trade-in',
        tradeACV: 0,
        tradeInAllowance: 0,
        payOff: 0,
      };
    }
    return fauxTradeIn;
  };

  const saveHandler = async () => {
    const errors = validateTradeIns(updatedTradeIns, tradeInFields);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
    } else if (
      dealer.crmType === CrmType.STANDALONE &&
      isUpdatingExistingTradeIns(initialTradeIns, updatedTradeIns)
    ) {
      setIsDeleteConfirmationModalOpen(true);
    } else {
      await onSave();
    }
  };

  const onSave = async () => {
    // filter out trade ins that have been explicitly deselected
    const tradeInsToSave =
      updatedTradeIns.length === 0 ? [getFauxTradeIn()] : updatedTradeIns;
    setValidationErrors({});
    setIsLoading(true);
    await updateTradeIns(tradeInsToSave);
    setIsLoading(false);
    onClose();
  };

  const onCancel = () => {
    onClose();
  };

  const confirmDeleteHandler = async (): Promise<void> => {
    await onSave();
    setIsDeleteConfirmationModalOpen(false);
  };

  const closeConfirmationModalHandler = (): void => {
    setIsDeleteConfirmationModalOpen(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      style={scrollableModalStyles as any}
      ariaHideApp={false}
    >
      <CloseIcon onClick={onCancel} />
      <div className="tradeTitleContainer">
        <div className="tradeTitle">{header ?? 'Edit Trade-Ins'}</div>
        {isLoading && <Loader size="medium" active inline />}
      </div>
      <TradeInList
        fields={tradeInFields}
        dealerId={dealerId}
        tradeIns={tradeIns}
        setTradeIns={setTradeIns}
        updatedTradeIns={updatedTradeIns}
        setUpdatedTradeIns={setUpdatedTradeIns}
        setIsLoading={setIsLoading}
        validationErrors={validationErrors}
        canDeleteTradeIns={allowTradeInUpdates}
        canEditTradeIns={allowTradeInUpdates}
      />
      <div className="buttonContainer">
        <div className="addButtonContainer">
          {allowTradeInUpdates && (
            <button
              className="addButton capitalize-in-chairman"
              disabled={tradeIns.length >= MAX_TRADE_INS}
              onClick={onAdd}
              data-testid="addTradeInButton"
            >
              + Add new trade-in
            </button>
          )}
        </div>
        <button className="cancelButton  cc-button-outlined" onClick={onCancel}>
          {allowTradeInUpdates ? 'Cancel' : 'Exit'}
        </button>
        {allowTradeInUpdates && (
          <button
            className="saveButton cc-button-primary capitalize-in-chairman"
            onClick={saveHandler}
            data-testid="onSaveButton"
          >
            Save changes
          </button>
        )}
      </div>
      <ConfirmationModal
        onConfirm={confirmDeleteHandler}
        onClose={closeConfirmationModalHandler}
        confirmText="Yes"
        cancelText="No"
        open={isDeleteConfirmationModalOpen}
        title="Update Trade in"
        description="Trade in updates will not be reflected in the CRM, are you sure you want to proceed?"
      />
    </Modal>
  );
};

export default TradeInsModal;
